import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  CloudDownload,
  Description,
  Replay,
  Search,
  SystemUpdateAlt
} from '@material-ui/icons'
import { toast } from 'react-toastify'

import api from '../../services/api'
import toastError from '../../errors/toastError'
import ConfirmationModal from '../ConfirmationModal'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2)
  },
  title: {
    marginBottom: theme.spacing(1)
  },
  description: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  tableContainer: {
    maxHeight: 600
  },
  tableCell: {
    fontSize: '0.875rem'
  },
  nameCell: {
    fontSize: '0.875rem',
    fontWeight: 500
  },
  selfBadge: {
    marginLeft: theme.spacing(1)
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4)
  },
  warningText: {
    color: theme.palette.warning.main,
    textAlign: 'center',
    padding: theme.spacing(4)
  },
  actionsCell: {
    whiteSpace: 'nowrap'
  }
}))

const updateChipProps = check => {
  if (!check) {
    return { label: 'Não verificado', color: 'default' }
  }
  if (check.updateAvailable === true) {
    return { label: 'Atualização disponível', color: 'secondary' }
  }
  if (check.updateAvailable === false) {
    return { label: 'Atualizado', color: 'primary' }
  }
  return { label: check.message || 'Indisponível', color: 'default' }
}

const ContainersManager = () => {
  const classes = useStyles()
  const [dockerAvailable, setDockerAvailable] = useState(true)
  const [containers, setContainers] = useState([])
  const [checks, setChecks] = useState({})
  const [busy, setBusy] = useState({})
  const [loading, setLoading] = useState(true)
  const [confirmAction, setConfirmAction] = useState(null)
  const [logMenuAnchor, setLogMenuAnchor] = useState(null)
  const [logMenuContainer, setLogMenuContainer] = useState(null)

  const loadContainers = useCallback(async () => {
    setLoading(true)
    try {
      const { data: statusData } = await api.get('/docker/status')
      if (!statusData.available) {
        setDockerAvailable(false)
        setContainers([])
        return
      }
      setDockerAvailable(true)
      const { data } = await api.get('/docker/containers')
      setContainers(data)
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadContainers()
  }, [loadContainers])

  const handleCheck = async container => {
    setBusy(prev => ({ ...prev, [container.id]: 'check' }))
    try {
      const { data } = await api.get(
        `/docker/containers/${container.id}/check-update`
      )
      setChecks(prev => ({ ...prev, [container.id]: data }))
      if (data.updateAvailable === true) {
        toast.info(`Atualização disponível para ${container.name}`)
      }
    } catch (err) {
      toastError(err)
    } finally {
      setBusy(prev => ({ ...prev, [container.id]: null }))
    }
  }

  const handleCheckAll = async () => {
    for (const container of containers.filter(c => c.state === 'running')) {
      // eslint-disable-next-line no-await-in-loop
      await handleCheck(container)
    }
  }

  const handlePull = async container => {
    setBusy(prev => ({ ...prev, [container.id]: 'pull' }))
    try {
      await api.post(`/docker/containers/${container.id}/pull`)
      toast.success(`Pull da imagem ${container.image} concluído`)
    } catch (err) {
      toastError(err)
    } finally {
      setBusy(prev => ({ ...prev, [container.id]: null }))
    }
  }

  const handleUpdate = async container => {
    setBusy(prev => ({ ...prev, [container.id]: 'update' }))
    try {
      const { data } = await api.post(
        `/docker/containers/${container.id}/update`
      )
      if (data.self) {
        toast.info(
          'O backend está sendo atualizado e será reiniciado. Aguarde alguns instantes e recarregue a página.'
        )
      } else if (data.updated) {
        toast.success(`${container.name}: ${data.message}`)
      } else {
        toast.info(`${container.name}: ${data.message}`)
      }
      await loadContainers()
    } catch (err) {
      toastError(err)
    } finally {
      setBusy(prev => ({ ...prev, [container.id]: null }))
    }
  }

  const handleRestart = async container => {
    setBusy(prev => ({ ...prev, [container.id]: 'restart' }))
    try {
      await api.post(`/docker/containers/${container.id}/restart`)
      if (container.self) {
        toast.info(
          'O backend está reiniciando. Aguarde alguns instantes e recarregue a página.'
        )
      } else {
        toast.success(`${container.name} reiniciado`)
      }
    } catch (err) {
      if (container.self) {
        toast.info(
          'O backend está reiniciando. Aguarde alguns instantes e recarregue a página.'
        )
      } else {
        toastError(err)
      }
    } finally {
      setBusy(prev => ({ ...prev, [container.id]: null }))
    }
  }

  const openConfirm = (container, action) => {
    setConfirmAction({ container, action })
  }

  const openLogMenu = (event, container) => {
    setLogMenuAnchor(event.currentTarget)
    setLogMenuContainer(container)
  }

  const closeLogMenu = () => {
    setLogMenuAnchor(null)
    setLogMenuContainer(null)
  }

  const handleDownloadLogs = async range => {
    if (!logMenuContainer) return
    closeLogMenu()
    setBusy(prev => ({ ...prev, [logMenuContainer.id]: `logs-${range}` }))
    try {
      const { data } = await api.get(
        `/docker/containers/${logMenuContainer.id}/logs?range=${range}`,
        { responseType: 'blob' }
      )
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `${logMenuContainer.name}-${range}-logs.txt`
      )
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      toastError(err)
    } finally {
      setBusy(prev => ({ ...prev, [logMenuContainer.id]: null }))
    }
  }

  const handleConfirm = () => {
    if (!confirmAction) return
    const { container, action } = confirmAction
    setConfirmAction(null)
    if (action === 'update') {
      handleUpdate(container)
    } else if (action === 'restart') {
      handleRestart(container)
    }
  }

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    )
  }

  if (!dockerAvailable) {
    return (
      <Typography className={classes.warningText}>
        Serviço Docker indisponível neste servidor. Verifique se o socket do
        Docker está montado no container do backend.
      </Typography>
    )
  }

  return (
    <div className={classes.container}>
      <Typography variant="h5" className={classes.title}>
        Containers Docker
      </Typography>
      <Typography variant="body2" className={classes.description}>
        Gerencie os containers do servidor: verifique atualizações de imagem,
        faça pull, atualize (pull + recriação, como o Watchtower) ou reinicie.
      </Typography>

      <div className={classes.toolbar}>
        <ButtonSecondary onClick={loadContainers} label="Atualizar lista" />
        <ButtonPrimary
          onClick={handleCheckAll}
          label="Verificar atualizações"
        />
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableCell}>Nome</TableCell>
              <TableCell className={classes.tableCell}>Imagem</TableCell>
              <TableCell className={classes.tableCell}>Estado</TableCell>
              <TableCell className={classes.tableCell}>Criado em</TableCell>
              <TableCell className={classes.tableCell}>Atualização</TableCell>
              <TableCell className={classes.tableCell} align="right">
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {containers.map(container => {
              const check = checks[container.id]
              const chip = updateChipProps(check)
              const rowBusy = busy[container.id]
              const isRunning = container.state === 'running'

              return (
                <TableRow key={container.id} hover>
                  <TableCell className={classes.nameCell}>
                    {container.name}
                    {container.self && (
                      <Chip
                        className={classes.selfBadge}
                        label="este backend"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {container.image}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Tooltip title={container.status || ''}>
                      <Chip
                        label={container.state}
                        size="small"
                        color={isRunning ? 'primary' : 'default'}
                        variant={isRunning ? 'default' : 'outlined'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {container.created
                      ? new Date(container.created * 1000).toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Tooltip title={check?.message || check?.remoteDigest || ''}>
                      <Chip label={chip.label} size="small" color={chip.color} />
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.actionsCell} align="right">
                    <Tooltip title="Verificar atualização">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleCheck(container)}
                          disabled={!!rowBusy || !isRunning}
                        >
                          {rowBusy === 'check' ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Search fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Pull da imagem">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handlePull(container)}
                          disabled={!!rowBusy || !isRunning}
                        >
                          {rowBusy === 'pull' ? (
                            <CircularProgress size={18} />
                          ) : (
                            <CloudDownload fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Atualizar (pull + recriar)">
                      <span>
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => openConfirm(container, 'update')}
                          disabled={!!rowBusy || !isRunning}
                        >
                          {rowBusy === 'update' ? (
                            <CircularProgress size={18} />
                          ) : (
                            <SystemUpdateAlt fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Reiniciar">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openConfirm(container, 'restart')}
                          disabled={!!rowBusy || !isRunning}
                        >
                          {rowBusy === 'restart' ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Replay fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Baixar logs">
                      <span>
                        <IconButton
                          size="small"
                          onClick={event => openLogMenu(event, container)}
                          disabled={!!rowBusy}
                        >
                          {rowBusy?.startsWith('logs-') ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Description fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={logMenuAnchor}
        open={Boolean(logMenuAnchor)}
        onClose={closeLogMenu}
      >
        <MenuItem onClick={() => handleDownloadLogs('full')}>
          Log completo
        </MenuItem>
        <MenuItem onClick={() => handleDownloadLogs('7days')}>
          Últimos 7 dias
        </MenuItem>
        <MenuItem onClick={() => handleDownloadLogs('5days')}>
          Últimos 5 dias
        </MenuItem>
        <MenuItem onClick={() => handleDownloadLogs('3days')}>
          Últimos 3 dias
        </MenuItem>
      </Menu>

      {confirmAction && (
        <ConfirmationModal
          open={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirm}
          title={
            confirmAction.action === 'update'
              ? `Atualizar ${confirmAction.container.name}`
              : `Reiniciar ${confirmAction.container.name}`
          }
        >
          {confirmAction.action === 'update'
            ? `Será feito o pull da imagem "${confirmAction.container.image}" e o container será recriado com a nova versão.${confirmAction.container.self
              ? ' Este é o container do backend: a aplicação ficará indisponível por alguns instantes.'
              : ''
            } Deseja continuar?`
            : `O container "${confirmAction.container.name}" será reiniciado.${confirmAction.container.self
              ? ' Este é o container do backend: a aplicação ficará indisponível por alguns instantes.'
              : ''
            } Deseja continuar?`}
        </ConfirmationModal>
      )}
    </div>
  )
}

export default ContainersManager
